// IntegrationScanPage.js
import React, { useState } from 'react';
import { Smartphone } from "lucide-react";
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import {
  Globe,
  Network,
  Shield,
  Clock,
  Target,
  ChevronRight,
  Server,
  AlertTriangle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/* ===================== Global Styles ===================== */

const GlobalStyle = createGlobalStyle`
  *, *::before, *::after {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  body {
    background: #111827;
    color: #f3f4f6;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    line-height: 1.5;
  }
`;

/* ===================== Animations ===================== */

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ===================== Page & Layout ===================== */

const PageContainer = styled.div`
  min-height: 100vh;
//   background: linear-gradient(135deg, #1f2937, #111827, #000);
  padding: 2rem 1rem;
`;

const MainContent = styled.main`
  max-width: 1120px;
  margin: 0 auto;
  animation: ${fadeIn} 0.8s ease-out;
`;

const SectionHeader = styled.div`
  margin-bottom: 2rem;
  text-align: center;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: #9ca3af;
`;

/* ===================== Scan Types Grid & Card ===================== */

const ScanGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 3rem;
  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ScanCard = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 1.5rem;
  border: ${(props) => props.border};
  background: ${(props) => props.bgGradientNormal};
  padding: 1.5rem;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: transform 0.3s ease, box-shadow 0.3s ease, background 0.3s ease;
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  
  &:hover {
    background: ${(props) => props.bgGradientHover};
    transform: ${(props) => (props.$disabled ? 'none' : 'translateY(-4px) scale(1.02)')};
    box-shadow: ${(props) => (props.$disabled ? 'none' : '0 8px 16px rgba(0, 0, 0, 0.3)')};
  }
  
  ${(props) =>
    props.selected &&
    `
      box-shadow: 0 0 0 3px #3b82f6;
  `}
`;

const CardInner = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const CardIconContainer = styled.div`
  flex-shrink: 0;
`;

const IconBox = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, #1f2937, #111827);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #374151;
`;

const ScanDetails = styled.div`
  flex-grow: 1;
`;

const ScanTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const ScanDescription = styled.p`
  color: #9ca3af;
  margin-bottom: 1rem;
  font-size: 1rem;
`;

const FeaturesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const FeatureBadge = styled.span`
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  background: rgba(31, 41, 55, 0.6);
  border: 1px solid #374151;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5rem;
`;

const TimeInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: #9ca3af;
  font-size: 0.875rem;
`;

const ConfigureButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #60a5fa;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  font-size: 0.875rem;
  
  &:hover {
    color: #3b82f6;
    transform: translateX(4px);
  }
`;

/* ===================== Quick Stats ===================== */

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 3rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const StatsCard = styled.div`
  background: linear-gradient(
      to bottom right,
      rgba(31, 41, 55, 0.6),
      rgba(17, 24, 39, 0.6)
    );
  border: 1px solid #1f2937;
  border-radius: 1rem;
  padding: 1.5rem;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.3);
  }
`;

const StatsContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const StatsIcon = styled.div`
  padding: 0.75rem;
  border-radius: 0.5rem;
  background: ${({ bg }) => bg};
  border: 1px solid ${({ borderColor }) => borderColor};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatsText = styled.div`
  h3 {
    font-size: 1rem;
    font-weight: 500;
    margin: 0;
  }
  p {
    font-size: 0.875rem;
    color: #9ca3af;
    margin: 0;
  }
`;

/* ===================== IntegrationScanPage Component ===================== */

const IntegrationScanPage = () => {
  const navigate = useNavigate();
  const [selectedScan, setSelectedScan] = useState(null);

  // Data for the different scan types
  const scanTypes = [
    {
      id: 'website',
      name: 'Web Application Scan',
      icon: <Globe size={32} />,
      description: 'OWASP Top 10, XSS, SQLi, CSRF',
      features: [
        'Vulnerability Assessment',
        'Security Misconfigurations',
        'Authentication Testing'
      ],
      time: '45-60 min',
      bgGradient: {
        normal:
          'linear-gradient(135deg, rgba(37, 99, 235, 0.2), rgba(30, 64, 175, 0.2))',
        hover:
          'linear-gradient(135deg, rgba(37, 99, 235, 0.3), rgba(30, 64, 175, 0.3))'
      },
      border: '1px solid rgba(59, 130, 246, 0.2)'
    },
     {
      id: 'firewall',
      name: 'Firewall Assessment',
      icon: <Shield size={32} />,
      description: 'Firewall Rules & Policies',
      features: ['Rule Analysis', 'Policy Verification', 'Security Gaps'],
      time: '20-30 min',
      bgGradient: {
        normal:
          'linear-gradient(135deg, rgba(220, 38, 38, 0.2), rgba(153, 27, 27, 0.2))',
        hover:
          'linear-gradient(135deg, rgba(220, 38, 38, 0.3), rgba(153, 27, 27, 0.3))'
      },
      border: '1px solid rgba(239, 68, 68, 0.2)',
      // disabled: true
    },
    {
      id: 'network',
      name: 'Network Infrastructure',
      icon: <Network size={32} />,
      description: 'Port Scanning, Service Detection',
      features: [
        'Open Port Analysis',
        'Service Enumeration',
        'Network Mapping'
      ],
      time: '30-45 min',
      bgGradient: {
        normal:
          'linear-gradient(135deg, rgba(147, 51, 234, 0.2), rgba(107, 33, 168, 0.2))',
        hover:
          'linear-gradient(135deg, rgba(147, 51, 234, 0.3), rgba(107, 33, 168, 0.3))'
      },
      border: '1px solid rgba(168, 85, 247, 0.2)',
      disabled: true
    },
    {
      id: 'api',
      name: 'API Security Testing',
      icon: <Server size={32} />,
      description: 'REST/GraphQL API Security',
      features: [
        'Endpoint Security',
        'Authentication Checks',
        'Data Validation'
      ],
      time: '25-35 min',
      bgGradient: {
        normal:
          'linear-gradient(135deg, rgba(5, 150, 105, 0.2), rgba(4, 120, 87, 0.2))',
        hover:
          'linear-gradient(135deg, rgba(5, 150, 105, 0.3), rgba(4, 120, 87, 0.3))'
      },
      border: '1px solid rgba(16, 185, 129, 0.2)',
      disabled: true
    },
    {
      id: 'mobile',
      name: 'Mobile Application Pentesting',
      icon: <Smartphone size={32} />,
      description: 'iOS & Android Security Testing',
      features: [
        'Static Code Analysis',
        'Runtime Security',
        'Data Storage Security'
      ],
      time: '60-90 min',
       bgGradient: {
        normal:
          'linear-gradient(135deg, rgba(234, 88, 12, 0.2), rgba(154, 52, 18, 0.2))',
        hover:
          'linear-gradient(135deg, rgba(234, 88, 12, 0.3), rgba(154, 52, 18, 0.3))'
      },
      border: '1px solid rgba(249, 115, 22, 0.2)',
      disabled: true
      }
    // {
    //   id: 'firewall',
    //   name: 'Firewall Assessment',
    //   icon: <Shield size={32} />,
    //   description: 'Firewall Rules & Policies',
    //   features: ['Rule Analysis', 'Policy Verification', 'Security Gaps'],
    //   time: '20-30 min',
    //   bgGradient: {
    //     normal:
    //       'linear-gradient(135deg, rgba(220, 38, 38, 0.2), rgba(153, 27, 27, 0.2))',
    //     hover:
    //       'linear-gradient(135deg, rgba(220, 38, 38, 0.3), rgba(153, 27, 27, 0.3))'
    //   },
    //   border: '1px solid rgba(239, 68, 68, 0.2)',
    //   disabled: true
    // }
  ];

  return (
    <>
      {/* <GlobalStyle /> */}
      <PageContainer>
        {/* <MainContent> */}
          <ScanGrid>
            {scanTypes.map((scan) => (
              <ScanCard
                key={scan.id}
                onClick={() => !scan.disabled && setSelectedScan(scan.id)}
                border={scan.border}
                bgGradientNormal={scan.bgGradient.normal}
                bgGradientHover={scan.bgGradient.hover}
                selected={selectedScan === scan.id}
                $disabled={scan.disabled}
              >
                <CardInner>
                  <CardIconContainer>
                    <IconBox>{scan.icon}</IconBox>
                  </CardIconContainer>
                  <ScanDetails>
                    <ScanTitle>{scan.name}</ScanTitle>
                    <ScanDescription>{scan.description}</ScanDescription>
                    <FeaturesContainer>
                      {scan.features.map((feature, index) => (
                        <FeatureBadge key={index}>{feature}</FeatureBadge>
                      ))}
                    </FeaturesContainer>
                    <CardFooter>
                      <TimeInfo>
                        <Clock size={16} style={{ marginRight: '4px' }} />
                        <span>{scan.time}</span>
                      </TimeInfo>
                      <ConfigureButton onClick={(e) => {
                        e.stopPropagation();
                        !scan.disabled && navigate("/scan-configure");
                      }}>
                        <span>Configure</span>
                        <ChevronRight size={16} />
                      </ConfigureButton>
                    </CardFooter>
                  </ScanDetails>
                </CardInner>
              </ScanCard>
            ))}
          </ScanGrid>
        {/* </MainContent> */}
      </PageContainer>
    </>
  );
};

export default IntegrationScanPage;
